import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ArtistApplication {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  portfolio_link: string;
  art_type: string;
  bio: string;
  why_brutt: string;
  collaboration_model: string;
  image_1_url: string | null;
  image_2_url: string | null;
  image_3_url: string | null;
  status: 'pending' | 'reviewed' | 'approved' | 'rejected';
  created_at: string;
  updated_at: string;
}

export interface CreateArtistApplicationData {
  full_name: string;
  email: string;
  phone?: string;
  portfolio_link: string;
  art_type: string;
  bio: string;
  why_brutt: string;
  collaboration_model: string;
  image_1_url?: string;
  image_2_url?: string;
  image_3_url?: string;
  status?: 'pending' | 'reviewed' | 'approved' | 'rejected';
}

export interface UpdateArtistApplicationData extends Partial<CreateArtistApplicationData> {
  id: string;
}

export function useArtistApplications() {
  const [artistApplications, setArtistApplications] = useState<ArtistApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchArtistApplications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from("artist_applications")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setArtistApplications(data || []);
    } catch (err) {
      console.error("Error fetching artist applications:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch artist applications");
    } finally {
      setLoading(false);
    }
  };

  const createArtistApplication = async (applicationData: CreateArtistApplicationData): Promise<ArtistApplication | null> => {
    try {
      setError(null);
      
      const { data, error } = await supabase
        .from("artist_applications")
        .insert([{
          ...applicationData,
          status: applicationData.status ?? 'pending',
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Update local state
      setArtistApplications(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error("Error creating artist application:", err);
      setError(err instanceof Error ? err.message : "Failed to create artist application");
      return null;
    }
  };

  const updateArtistApplication = async (applicationData: UpdateArtistApplicationData): Promise<ArtistApplication | null> => {
    try {
      setError(null);
      
      const { id, ...updateData } = applicationData;
      
      const { data, error } = await supabase
        .from("artist_applications")
        .update(updateData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      
      // Update local state
      setArtistApplications(prev => prev.map(app => app.id === id ? data : app));
      return data;
    } catch (err) {
      console.error("Error updating artist application:", err);
      setError(err instanceof Error ? err.message : "Failed to update artist application");
      return null;
    }
  };

  const deleteArtistApplication = async (applicationId: string): Promise<boolean> => {
    try {
      setError(null);
      
      const { error } = await supabase
        .from("artist_applications")
        .delete()
        .eq("id", applicationId);

      if (error) throw error;
      
      // Update local state
      setArtistApplications(prev => prev.filter(app => app.id !== applicationId));
      return true;
    } catch (err) {
      console.error("Error deleting artist application:", err);
      setError(err instanceof Error ? err.message : "Failed to delete artist application");
      return false;
    }
  };

  const getArtistApplicationById = async (applicationId: string): Promise<ArtistApplication | null> => {
    try {
      setError(null);
      
      const { data, error } = await supabase
        .from("artist_applications")
        .select("*")
        .eq("id", applicationId)
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      console.error("Error fetching artist application:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch artist application");
      return null;
    }
  };

  const getApplicationsByStatus = (status: ArtistApplication['status']): ArtistApplication[] => {
    return artistApplications.filter(app => app.status === status);
  };

  const getPendingApplications = (): ArtistApplication[] => {
    return getApplicationsByStatus('pending');
  };

  const getReviewedApplications = (): ArtistApplication[] => {
    return getApplicationsByStatus('reviewed');
  };

  const getApprovedApplications = (): ArtistApplication[] => {
    return getApplicationsByStatus('approved');
  };

  const getRejectedApplications = (): ArtistApplication[] => {
    return getApplicationsByStatus('rejected');
  };

  useEffect(() => {
    fetchArtistApplications();
  }, []);

  return {
    artistApplications,
    loading,
    error,
    fetchArtistApplications,
    createArtistApplication,
    updateArtistApplication,
    deleteArtistApplication,
    getArtistApplicationById,
    getApplicationsByStatus,
    getPendingApplications,
    getReviewedApplications,
    getApprovedApplications,
    getRejectedApplications,
  };
}
